// ScanReviewSurface — the scan review surface (chip strip + inline
// size/brand/variant pickers + keyboard-attached input bar). Extracted
// verbatim from ScanPipeline.jsx (pure move).

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { findIngredient } from "../../../data/ingredients";
import { usePopularPackages } from "../../../lib/usePopularPackages";
import { brandsForCanonical } from "../../../data/brandExpertise";
import { useVariantObservations } from "../../../lib/useVariantObservations";
import { FONT, COLOR, CHIP_TONES } from "../../../lib/tokens";
import {
  AXES, HUBS_WITH_CUTS, SIZE_UNIT_OPTIONS,
  isFound, axisDisplay, placementBreadcrumb,
  RawScanPanel,
  fallbackSizesFor, mergeSuggestedAndFallback,
  Header, Bucket, FoundChip,
  primaryActionStyle, secondaryActionStyle, ghostActionStyle,
} from "./scanShared";

// ── Review surface — chip strip + raw data + actions ────────────────

export function ReviewSurface({ draft, rawScan, dbInfo, onPlace, onPhoto, onWizard, onRescan, onOpenEditor, onSetSize, onSetBrand, onSetVariant }) {
  // Per-axis applicability — cut chip only renders for meat
  // canonicals (chicken/beef/pork/turkey hubs). Other categories
  // (cheese, dairy, pantry, produce) skip the cut axis entirely.
  // Without this, cheese scans showed "+ cut" as MISSING even
  // though cheese has no anatomical cut concept.
  const ing = findIngredient(draft.canonicalId);
  const hasCutAxis = ing?.parentId && HUBS_WITH_CUTS.has(ing.parentId);
  const applicableAxes = AXES.filter((a) => {
    if (a.key === "cut" && !hasCutAxis) return false;
    return true;
  });
  const found = applicableAxes.filter((a) => isFound(draft, a, dbInfo));
  const missing = applicableAxes.filter((a) => !isFound(draft, a, dbInfo));
  const sizeFound = !!(draft.packageAmount && draft.packageUnit);
  const brandMissing = !draft.brand;
  const canPlace = !!draft.canonicalId && sizeFound;
  const breadcrumb = placementBreadcrumb(draft, dbInfo);

  // Inline size pills — same data path as the wizard's PackageSizeStep
  // (usePopularPackages → popular_package_sizes RPC + category
  // fallback). User asked for the pills to surface on the review
  // itself, not behind a "tap missing chip → open editor" round-trip.
  const { rows: sizeObserved } = usePopularPackages(draft.brand || null, draft.canonicalId || null, 6);
  const sizeFallback = useMemo(() => fallbackSizesFor(draft.canonicalId), [draft.canonicalId]);
  const sizeSuggested = useMemo(
    () => mergeSuggestedAndFallback(sizeObserved, sizeFallback, 6),
    [sizeObserved, sizeFallback],
  );

  // Inline brand pills — top brands for this canonical via the same
  // 5-tier brandsForCanonical merge the wizard uses. Limited to 6
  // so the pill row stays a single line. Tapping a pill calls
  // onSetBrand which updates the draft; the next render moves brand
  // to FOUND and the pill row disappears.
  const brandSuggested = useMemo(() => {
    if (!draft.canonicalId) return [];
    try {
      return brandsForCanonical(draft.canonicalId, { limit: 6 }).slice(0, 6);
    } catch { return []; }
  }, [draft.canonicalId]);

  // Observation tier — surfaces variants users have committed for
  // this (canonical, brand) before, sourced from manual_corrections.
  // Layered on top of the catalog tier below so a freshly-observed
  // "Wild Cherry" appears as a pill even if BRAND_EXPERTISE doesn't
  // declare it yet. See useVariantObservations.js for cache discipline.
  const { observations: variantObservations } = useVariantObservations(
    draft.canonicalId || null,
    draft.brand || null,
  );

  // Inline variant pills — surfaces the active brand's known variants
  // so the user can finish the (canonical, brand, variant) tuple
  // without leaving the review surface. The wizard has the same step
  // gated behind brand pick; mirror it here so UPC scans don't have
  // to bounce through the wizard just to disambiguate Sharp vs Mild,
  // Wild Cherry vs Original, etc.
  //
  // Two-tier merge:
  //   1. Catalog tier — BRAND_EXPERTISE + info.brands.common +
  //      derived sources via brandsForCanonical's catalog match.
  //   2. Observation tier — manual_corrections rows for this
  //      (canonical, brand) pair. Variant names users have committed
  //      surface as pills here even when the catalog hasn't caught up.
  // Deduped by lowercased name; observation entries with higher
  // correction_count rank ahead of catalog-only entries. Limited to
  // 8 so the pill row stays scannable.
  const variantSuggested = useMemo(() => {
    if (!draft.canonicalId || !draft.brand) return [];
    let catalogVariants = [];
    try {
      const catalog = brandsForCanonical(draft.canonicalId, {
        info: dbInfo,
        limit: 24,
      });
      const lower = String(draft.brand).trim().toLowerCase();
      const match = catalog.find((s) => (s.display || "").toLowerCase() === lower);
      catalogVariants = Array.isArray(match?.variants) ? match.variants : [];
    } catch { /* catalog miss — observations alone are still useful */ }

    const byKey = new Map(); // lowercased name → { name, count, fromCatalog }
    for (const v of catalogVariants) {
      const name = (v?.name || v?.fullName || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (byKey.has(key)) continue;
      byKey.set(key, { name, fullName: v.fullName || null, count: 0, fromCatalog: true });
    }
    for (const obs of variantObservations || []) {
      const name = (obs?.name || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const existing = byKey.get(key);
      if (existing) {
        existing.count = obs.count || existing.count;
      } else {
        byKey.set(key, { name, fullName: null, count: obs.count || 1, fromCatalog: false });
      }
    }
    // Sort: highest observation count first, then catalog order
    // preserved among ties.
    const merged = Array.from(byKey.values()).sort((a, b) => b.count - a.count);
    return merged.slice(0, 8);
  }, [draft.canonicalId, draft.brand, dbInfo, variantObservations]);
  // Show the picker only when brand is known, variant isn't set, and
  // the catalog has at least one variant to offer. The "user"
  // override flag (set when onSetVariant fires with empty or value)
  // hides the picker for the rest of the draft session even if the
  // user explicitly skipped.
  const variantPickerVisible =
    !!draft.brand
    && !draft.variant
    && !draft.overridden?.has?.("variant")
    && variantSuggested.length > 0;

  // Bar mode — explicitly opt-in. The keyboard-attached input bar is
  // ONLY shown when the user taps a "+ Custom" affordance on the
  // matching pill row. Defaulting it to whichever axis is missing
  // hijacks the keyboard the moment the review surface mounts, which
  // is exactly the friction the inline pills are meant to remove.
  const [barMode, setBarMode] = useState(null);
  const closeBar = () => setBarMode(null);

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 14,
      // Reserve room for the keyboard-attached input bar so its
      // fixed-position content doesn't cover the rest of the review
      // (PLACE button, raw panel) when it's open. Bar height ≈
      // 64-100px depending on size/brand mode.
      paddingBottom: barMode ? 120 : 0,
    }}>
      <Header
        upc={draft.barcodeUpc}
        kicker="SCAN REVIEW"
        title={
          draft.brand && draft.canonicalName
            ? `${draft.brand} · ${draft.canonicalName}`
            : draft.canonicalName || "What we found"
        }
      />

      {/* Placement breadcrumb — full hub-chain trail so the user can
          read where this canonical will land BEFORE pressing place.
          Same shape as the kitchen card's IdentityBreadcrumb but
          flattened to a single line. Walks canonical → parent hub
          chain → derived tile → location. Visible the moment a
          canonical is bound, even if the draft hasn't stored
          tileId/location yet (those are derivable from the chain). */}
      {breadcrumb && (
        <div style={breadcrumbStyle}>{breadcrumb}</div>
      )}

      <KeyboardInputBar
        mode={barMode}
        onSetSize={(amount, unit) => { onSetSize?.(amount, unit); closeBar(); }}
        onSetBrand={(value)        => { onSetBrand?.(value);      closeBar(); }}
        onClose={closeBar}
      />

      {(found.length > 0 || sizeFound) && (
        <Bucket label="FOUND" hint="Tap any to edit" tone={COLOR.leaf}>
          {found.map((axis) => (
            <FoundChip
              key={axis.key}
              tone={axis.tone}
              label={axis.label}
              value={axisDisplay(draft, axis, dbInfo)}
              onClick={() => onOpenEditor(axis.editor)}
            />
          ))}
          {sizeFound && (
            <FoundChip
              tone={CHIP_TONES.canonical}
              label="Size"
              value={`${draft.packageAmount} ${draft.packageUnit}`}
              onClick={() => onOpenEditor("size")}
            />
          )}
        </Bucket>
      )}

      {/* Inline size picker — surfaces top sizes for this canonical
          (brand-aware when a brand is bound) for one-tap fill. Same
          data path as wizard's PackageSizeStep. The "+ Custom" pill
          opens the keyboard-attached input bar; defaulting the bar
          open hijacks the keyboard the moment the screen mounts. */}
      {!sizeFound && (
        <SizeInlinePicker
          label={draft.brand ? `Pick a size · ${draft.brand}` : "Pick a size"}
          suggestions={sizeSuggested}
          onSet={(amount, unit) => onSetSize?.(amount, unit)}
          onCustom={() => setBarMode("size")}
        />
      )}

      {/* Inline brand picker — top brands for this canonical via the
          same 5-tier brandsForCanonical merge the wizard uses.
          "+ Custom" opens the keyboard-attached input bar for free-
          text brand entry. */}
      {brandMissing && (
        <BrandInlinePicker
          label="Pick a brand"
          suggestions={brandSuggested}
          onSet={(value) => onSetBrand?.(value)}
          onCustom={() => setBarMode("brand")}
        />
      )}

      {/* Inline variant picker — only renders when a brand is known
          AND the catalog has variants AND the user hasn't already
          set / explicitly skipped one. Same gated mirror of the
          wizard's variant step. */}
      {variantPickerVisible && (
        <VariantInlinePicker
          label={`Which ${draft.brand}?`}
          suggestions={variantSuggested}
          onSet={(name) => onSetVariant?.(name)}
          onSkip={() => onSetVariant?.("")}
        />
      )}

      {/* Missing-axis summary for what's left. Excludes axes the user
          has already filled inline (brand, size) and excludes axes
          that are derivable from the canonical's hub chain (tile,
          hub) since those resolve automatically at place-time. Only
          truly unknown axes show up here. */}
      {(() => {
        const derivable = new Set(["tileId", "hub", "location"]);
        const remaining = missing
          .filter((a) => a.key !== "brand")
          .filter((a) => !derivable.has(a.key))
          .map((a) => a.label.toLowerCase());
        if (remaining.length === 0) return null;
        return <div style={missingNoteStyle}>Missing: {remaining.join(", ")}</div>;
      })()}

      <RawScanPanel rawScan={rawScan} draft={draft} startOpen />

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
        {/* PLACE IN KITCHEN — primary CTA when canonical + size are
            set. Tile/hub/location auto-derive from the canonical's
            hub chain at place-time, so we don't gate this on them. */}
        {canPlace && (
          <button
            type="button"
            onClick={() => {
              console.info("[scan] Place tapped", {
                canonicalId: draft.canonicalId,
                packageAmount: draft.packageAmount,
                packageUnit: draft.packageUnit,
                tileId:   draft.tileId,
                location: draft.location,
                tileSource: draft.sources?.tileId,
                locSource:  draft.sources?.location,
              });
              onPlace?.();
            }}
            className="mcm-focusable"
            style={primaryActionStyle(true)}
          >
            PLACE IN KITCHEN
          </button>
        )}

        {/* Photo CTA — primary when the user can't place yet (size
            missing), since a photo of the package fills brand + size
            + nutrition in one shot. Demoted to secondary once the
            row is ready to place — the photo path still works for
            adding nutrition / claims, just not the headline action. */}
        <button
          type="button"
          onClick={onPhoto}
          className="mcm-focusable"
          style={canPlace ? secondaryActionStyle : primaryActionStyle(true)}
        >
          {canPlace
            ? "Take a photo to fill in more"
            : "TAKE A PHOTO — READS BRAND, SIZE, NUTRITION"}
        </button>

        {/* Wizard route — guided tap-to-place for any axes still
            missing. Hidden once the row is ready to place. */}
        {!canPlace && (
          <button
            type="button"
            onClick={onWizard}
            className="mcm-focusable"
            style={secondaryActionStyle}
          >
            Tap to fill in the wizard
          </button>
        )}

        <button type="button" onClick={onRescan} className="mcm-focusable" style={ghostActionStyle}>
          Scan a different barcode
        </button>
      </div>
    </div>
  );
}

const missingNoteStyle = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
  color: COLOR.muted, fontStyle: "italic",
  letterSpacing: "0.02em",
  padding: "6px 4px",
};

const breadcrumbStyle = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  color: COLOR.muted,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  padding: "2px 4px 0",
  marginTop: -8,  // pull up under the header so it reads as a kicker
};

// Shared label + pill-row primitives for the inline pickers. Pills
// commit on tap; the picker's row collapses once the axis lands in
// FOUND. The custom-entry input lives directly below the pills so
// the user can type a size / brand the suggestions don't cover
// without opening any drawer.
const inlinePickerLabelStyle = {
  fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
  color: COLOR.muted, letterSpacing: "0.16em", textTransform: "uppercase",
};
const inlinePickerPillStyle = (active = false) => ({
  padding: "8px 14px",
  borderRadius: 999,
  border: `1px solid ${active ? COLOR.leaf : COLOR.edge}`,
  background: active ? "rgba(47,143,131,0.14)" : "transparent",
  color: active ? COLOR.leaf : COLOR.ink,
  fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
  cursor: "pointer",
  touchAction: "manipulation",
});
const inlinePickerInputStyle = {
  flex: 1,
  minWidth: 0,
  padding: "10px 12px",
  fontFamily: FONT.forma, fontSize: 16, fontWeight: 500,
  color: COLOR.ink,
  background: COLOR.deep,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  outline: "none",
};

// Visible commit button next to the inline custom-entry inputs.
// `enterkeyhint="done"` makes the iOS keyboard's return key read
// "done"; the SET button is the explicit visual affordance for users
// who want a button to tap rather than the keyboard's return key.
const inlinePickerSubmitStyle = (enabled) => ({
  padding: "10px 16px",
  borderRadius: 10,
  border: `1px solid ${enabled ? COLOR.leaf : COLOR.edge}`,
  background: enabled ? COLOR.leaf : "transparent",
  color: enabled ? "#fff" : COLOR.muted,
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
  letterSpacing: "0.10em", textTransform: "uppercase",
  cursor: enabled ? "pointer" : "not-allowed",
  touchAction: "manipulation",
  flex: "0 0 auto",
});

// Dashed "+ Custom" pill — opens the keyboard-attached input bar.
// Visually distinct from suggestion pills so it reads as "type your
// own" rather than just another option.
const customPillStyle = {
  padding: "8px 14px",
  borderRadius: 999,
  border: `1px dashed ${COLOR.muted}`,
  background: "transparent",
  color: COLOR.muted,
  fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
  cursor: "pointer",
  touchAction: "manipulation",
};

// SizeInlinePicker — popular/fallback size pills + a "+ Custom"
// trigger. The custom-entry input itself lives in KeyboardInputBar
// (portaled to body, pinned to keyboard top) so what the user is
// typing rides above the keyboard and never gets clipped.
export function SizeInlinePicker({ label, suggestions, onSet, onCustom }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={inlinePickerLabelStyle}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(suggestions || []).map((s, i) => (
          <button
            key={`${s.amount}|${s.unit}|${i}`}
            type="button"
            onClick={() => onSet?.(Number(s.amount), s.unit)}
            className="mcm-focusable"
            style={inlinePickerPillStyle(false)}
          >
            {s.amount} {s.unit}
          </button>
        ))}
        {onCustom && (
          <button
            type="button"
            onClick={onCustom}
            className="mcm-focusable"
            style={customPillStyle}
          >
            + Custom
          </button>
        )}
      </div>
    </div>
  );
}

// BrandInlinePicker — popular brand pills + a "+ Custom" trigger.
// Free-text brand entry lives in KeyboardInputBar.
export function BrandInlinePicker({ label, suggestions, onSet, onCustom }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={inlinePickerLabelStyle}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(suggestions || []).map((b, i) => (
          <button
            key={`${b.slug || b.display}|${i}`}
            type="button"
            onClick={() => onSet?.(b.display || b.slug)}
            className="mcm-focusable"
            style={inlinePickerPillStyle(false)}
          >
            {b.display || b.slug}
          </button>
        ))}
        {onCustom && (
          <button
            type="button"
            onClick={onCustom}
            className="mcm-focusable"
            style={customPillStyle}
          >
            + Custom
          </button>
        )}
      </div>
    </div>
  );
}

// VariantInlinePicker — pill row of variant names for the active
// brand (Pepsi → Wild Cherry / Diet / Original / Zero Sugar, etc.).
// Mirrors the wizard's variant step but inline on the review surface
// so UPC scans don't have to bounce through the wizard.
//
// "Skip" sets variant to "" — the store's "user" override flag
// (driven by `source: "user"`) gates the picker from re-opening even
// though the stored value is null. Without that, an unset variant
// would re-trigger the picker on every render.
export function VariantInlinePicker({ label, suggestions, onSet, onSkip }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={inlinePickerLabelStyle}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(suggestions || []).map((v, i) => {
          const name = v.name || v.fullName || "";
          if (!name) return null;
          return (
            <button
              key={`${name}|${i}`}
              type="button"
              onClick={() => onSet?.(name)}
              className="mcm-focusable"
              style={inlinePickerPillStyle(false)}
            >
              {name}
            </button>
          );
        })}
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            className="mcm-focusable"
            style={customPillStyle}
          >
            No variant
          </button>
        )}
      </div>
    </div>
  );
}

// KeyboardInputBar — a fixed-position input bar pinned to the top of
// the iOS keyboard. App.jsx mirrors the keyboard height into the
// `--kb-h` CSS variable; this bar reads it as `bottom: var(--kb-h)`
// so the bar lifts when the keyboard rises and what the user types
// can never be hidden behind the keyboard.
//
// Modes:
//   "size"  — amount input + unit dropdown + SET. Unit defaults to
//             "oz" but a horizontally-scrolled pill row inside the
//             bar lets the user switch without leaving the bar.
//   "brand" — free-text input + SET.
//   null    — bar hidden.
export function KeyboardInputBar({ mode, onSetSize, onSetBrand, onClose }) {
  const [amount, setAmount] = useState("");
  const [unit, setUnit]     = useState("oz");
  const [text, setText]     = useState("");

  // Reset the local input when the bar swaps modes (size → brand on
  // size commit). Otherwise the previous typed value lingers.
  useEffect(() => {
    setAmount("");
    setText("");
  }, [mode]);

  if (!mode) return null;

  const sizeValid  = Number(amount) > 0 && !!unit;
  const brandValid = text.trim().length > 0;
  const submitSize  = () => { if (sizeValid)  { onSetSize?.(Number(amount), unit); setAmount(""); } };
  const submitBrand = () => { if (brandValid) { onSetBrand?.(text.trim());        setText(""); } };

  const barWrap = {
    position: "fixed",
    left: 0, right: 0,
    bottom: "var(--kb-h, 0px)",
    zIndex: 1200,
    background: COLOR.night,
    borderTop: `1px solid ${COLOR.border}`,
    boxShadow: "0 -10px 24px rgba(0,0,0,0.18)",
    padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))",
    display: "flex", flexDirection: "column", gap: 8,
    transition: "bottom 180ms ease",
  };

  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="mcm-focusable"
      style={{
        flex: "0 0 auto",
        width: 36, height: 36,
        borderRadius: 999,
        border: `1px solid ${COLOR.edge}`,
        background: "transparent",
        color: COLOR.muted,
        fontFamily: FONT.forma, fontSize: 14, fontWeight: 700,
        cursor: "pointer",
        touchAction: "manipulation",
      }}
    >
      ×
    </button>
  );

  return createPortal(
    mode === "size" ? (
      <div style={barWrap}>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {closeButton}
          <input
            autoFocus
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitSize(); } }}
            placeholder={`Type a size in ${unit}`}
            style={inlinePickerInputStyle}
          />
          <button
            type="button"
            onClick={submitSize}
            disabled={!sizeValid}
            className="mcm-focusable"
            style={inlinePickerSubmitStyle(sizeValid)}
          >
            Set
          </button>
        </div>
        <div style={{
          display: "flex", flexWrap: "nowrap", gap: 6,
          overflowX: "auto",
          paddingBottom: 2,
        }}>
          {SIZE_UNIT_OPTIONS.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => setUnit(u)}
              className="mcm-focusable"
              style={{ ...inlinePickerPillStyle(u === unit), flex: "0 0 auto" }}
            >
              {u}
            </button>
          ))}
        </div>
      </div>
    ) : (
      <div style={barWrap}>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {closeButton}
          <input
            autoFocus
            type="text"
            enterKeyHint="done"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitBrand(); } }}
            placeholder="Type a brand"
            style={inlinePickerInputStyle}
            autoComplete="off"
            autoCapitalize="words"
          />
          <button
            type="button"
            onClick={submitBrand}
            disabled={!brandValid}
            className="mcm-focusable"
            style={inlinePickerSubmitStyle(brandValid)}
          >
            Set
          </button>
        </div>
      </div>
    ),
    document.body,
  );
}
